import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { Plus, Beef, Trash2, Edit, Scale, X, AlertOctagon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { overlayClose } from '../lib/overlayClose';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TypePiecesManager } from '../components/TypePiecesManager';

const BEEF_PAYMENT_METHODS = [
  { value: 'boleto', label: 'Boleto (a prazo → Conta a Pagar pendente)' },
  { value: 'dinheiro', label: 'Dinheiro (à vista → já quitada)' },
  { value: 'pix', label: 'PIX (à vista → já quitada)' },
  { value: 'cartao', label: 'Cartão (à vista → já quitada)' },
  { value: 'transferencia', label: 'Transferência (à vista → já quitada)' },
  { value: 'cheque', label: 'Cheque (à vista → já quitada)' },
];
const fmtBrDate = (iso) => { try { return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }); } catch { return '—'; } };

const PaymentFields = ({ value, onChange, totalCost, costPerKg, testPrefix }) => {
  const isBoleto = value.payment_method === 'boleto';
  if (!(costPerKg > 0)) return null;
  return (
    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3 bg-amber-50/60 border border-amber-200 rounded-md p-3" data-testid={`${testPrefix}-payment-box`}>
      <div className="space-y-1">
        <Label className="text-xs">Nº da Nota (opcional)</Label>
        <Input value={value.note_number} onChange={(e) => onChange({ ...value, note_number: e.target.value })} data-testid={`${testPrefix}-note-number`} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Forma de Pagamento *</Label>
        <Select value={value.payment_method} onValueChange={(v) => onChange({ ...value, payment_method: v })}>
          <SelectTrigger data-testid={`${testPrefix}-payment-method`}><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            {BEEF_PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value} data-testid={`${testPrefix}-payment-method-${m.value}`}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {isBoleto && (
        <div className="space-y-1">
          <Label className="text-xs">Vencimento do Boleto *</Label>
          <Input type="date" value={value.due_date} onChange={(e) => onChange({ ...value, due_date: e.target.value })} data-testid={`${testPrefix}-due-date`} />
        </div>
      )}
      <p className="md:col-span-3 text-xs text-amber-900" data-testid={`${testPrefix}-payment-info`}>
        Ao confirmar, será gerada a <b>Conta a Pagar de R$ {totalCost.toFixed(2)}</b>{value.payment_method ? (isBoleto ? <> como <b>PENDENTE</b> com vencimento em <b>{value.due_date ? fmtBrDate(value.due_date) : '(informe a data)'}</b>.</> : <> já <b>QUITADA</b> (paga à vista).</>) : '.'}
      </p>
    </div>
  );
};

const pieceKeyOf = (name) => (name || '').trim().toLowerCase().replace(/\s+/g, ' ');

const PieceCodeLink = ({ piece, link, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const tid = piece.replace(/[^a-z0-9]/gi, '-').toLowerCase();
  const save = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await axios.put(`${API}/beef/piece-links`, { piece_name: piece, product_code: code.trim() }, { withCredentials: true });
      setMsg(''); setEditing(false); setCode('');
      onSaved(data);
    } catch (err) { setMsg(err.response?.data?.detail || 'Erro ao vincular'); }
  };
  if (link && !editing) {
    return (
      <button type="button" onClick={() => { setEditing(true); setCode(link.product_code || ''); }} className="text-[11px] text-emerald-700 hover:underline text-left" title="Clique para alterar o código" data-testid={`piece-link-${tid}`}>
        cód. <b>{link.product_code}</b> → {link.product_name}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') setEditing(false); }}
        placeholder="cód. balança"
        className="h-6 w-24 text-[11px] border border-amber-300 rounded px-1 bg-white"
        data-testid={`piece-code-input-${tid}`}
      />
      <button type="button" onClick={save} className="text-[11px] px-1.5 h-6 rounded bg-amber-500 text-white font-bold" data-testid={`piece-code-save-${tid}`}>OK</button>
      {link && (
        <button type="button" onClick={async () => { await axios.delete(`${API}/beef/piece-links`, { params: { piece_name: piece }, withCredentials: true }); setEditing(false); onSaved(null, piece); }} className="text-[11px] px-1.5 h-6 rounded border border-red-300 text-red-600" data-testid={`piece-code-remove-${tid}`}>remover</button>
      )}
      {msg && <span className="text-[10px] text-red-600">{msg}</span>}
      {!link && !msg && <span className="text-[10px] text-amber-700">sem código · entra por nome</span>}
    </div>
  );
};

const PayableResult = ({ payable }) => payable ? (
  <div className="mt-2" data-testid="beef-payable-result">
    Conta a Pagar gerada: <b>R$ {Number(payable.amount).toFixed(2)}</b> · {payable.supplier_name} · {payable.status === 'pendente' ? <>status <b>PENDENTE</b>, vencimento {fmtBrDate(payable.due_date)}</> : <>status <b>QUITADA</b> ({payable.payment_method})</>}
  </div>
) : null;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Peças por tipo de carcaça (o backend soma o peso ao estoque do produto de mesmo nome;
// "limpeza/desossa" -> produto "Sebo"; "quebra" -> produto "Linguiça").
const PIECES_DIANTEIRO = ['acém/paleta', 'miolo de acém', 'miolo de paleta', 'ponta de peito', 'costela', 'músculo', 'pucheiro', 'limpeza/desossa', 'quebra'];
const PIECES_TRASEIRO = ['fraldinha', 'filé mignon', 'contra filé', 'alcatra', 'picanha', 'coxão mole', 'ponta de alcatra', 'capa do contra filé', 'bisteca', 'patinho', 'músculo', 'limpeza/desossa', 'quebra'];
const dedup = (arr) => arr.filter((v, i) => arr.indexOf(v) === i);
const PIECES_VACA_CASADA = dedup([...PIECES_DIANTEIRO.filter(p => !['limpeza/desossa', 'quebra'].includes(p)), ...PIECES_TRASEIRO]);
const CARCASS_TYPES = [
  { key: 'dianteiro', label: 'Dianteiro', pieces: PIECES_DIANTEIRO },
  { key: 'traseiro', label: 'Traseiro', pieces: PIECES_TRASEIRO },
  { key: 'vaca_casada', label: 'Vaca Casada', pieces: PIECES_VACA_CASADA },
  { key: 'vaca_casada_gorda', label: 'Vaca Casada Gorda', pieces: PIECES_VACA_CASADA },
];

export const FracionamentoPage = () => (
  <Layout>
    <FracionamentoContent />
  </Layout>
);

export const FracionamentoContent = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('arrivals');
  const [cuts, setCuts] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showCutForm, setShowCutForm] = useState(false);
  const [showArrivalForm, setShowArrivalForm] = useState(false);
  const [editingCut, setEditingCut] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Modal de exclusão de fracionamento (arrival)
  const [deleteArrival, setDeleteArrival] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');

  const [cutForm, setCutForm] = useState({ name: '', yield_percent: '', sale_price_kg: '', product_code: '', notes: '' });
  const [arrivalForm, setArrivalForm] = useState({
    arrival_date: new Date().toISOString().substring(0, 10),
    total_weight: '', supplier_name: '', cost_per_kg: '', notes: ''
  });
  const [arrivalPay, setArrivalPay] = useState({ note_number: '', payment_method: '', due_date: '' });
  const [entryType, setEntryType] = useState('peca'); // peca | dianteiro | traseiro | vaca_casada | vaca_casada_gorda
  const [arrivalResult, setArrivalResult] = useState(null);

  const [calibTotal, setCalibTotal] = useState('');
  const [calibCuts, setCalibCuts] = useState([{ name: '', weight_kg: '', sale_price_kg: '' }]);
  const [calibResult, setCalibResult] = useState(null);
  // Entrada de Carcaça
  const [carcassType, setCarcassType] = useState('dianteiro');
  const [productList, setProductList] = useState([]);
  const [typeCalibs, setTypeCalibs] = useState({}); // { carcass_type: { pieces, tolerance_percent, ... } }
  const [typePieces, setTypePieces] = useState({}); // { carcass_type: [{name, code, product_name}] } — peças cadastradas pelo usuário
  // Modal de calibração por tipo
  const [showCalibType, setShowCalibType] = useState(false);
  const [calibTypeTotal, setCalibTypeTotal] = useState('');
  const [calibTypeTolerance, setCalibTypeTolerance] = useState('5');
  const [calibTypeWeights, setCalibTypeWeights] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [cRes, aRes, sRes] = await Promise.all([
        axios.get(`${API}/beef/cuts`, { withCredentials: true }),
        axios.get(`${API}/beef/arrivals`, { withCredentials: true }),
        axios.get(`${API}/beef/summary`, { withCredentials: true })
      ]);
      setCuts(cRes.data);
      setArrivals(aRes.data);
      setSummary(sRes.data);
    } catch (err) { console.error(err); }
    try {
      const tcRes = await axios.get(`${API}/beef/type-calibrations`, { withCredentials: true });
      setTypeCalibs(tcRes.data || {});
    } catch (err) { console.error(err); }
    try {
      const tpRes = await axios.get(`${API}/beef/type-pieces`, { withCredentials: true });
      setTypePieces(tpRes.data || {});
    } catch (err) { console.error(err); }
    try {
      const pRes = await axios.get(`${API}/products?limit=5000`, { withCredentials: true });
      const list = Array.isArray(pRes.data) ? pRes.data : (pRes.data.products || pRes.data.items || []);
      setProductList(list);
    } catch (err) { console.error(err); }
  };

  // Peças do tipo: lista cadastrada pelo usuário (Gerenciar peças) ou a padrão
  const defaultPieces = (CARCASS_TYPES.find(t => t.key === carcassType) || CARCASS_TYPES[0]).pieces;
  const customPieces = typePieces[carcassType];
  const currentPieces = customPieces ? customPieces.map(p => p.name) : defaultPieces;
  const currentPieceObjs = customPieces || defaultPieces.map(n => ({ name: n, code: '' }));
  // Calibração do tipo selecionado
  const currentCalib = typeCalibs[carcassType];
  const openCalibType = () => {
    setError('');
    const c = typeCalibs[carcassType];
    if (c) {
      setCalibTypeTotal(String(c.reference_total_weight || ''));
      setCalibTypeTolerance(String(c.tolerance_percent ?? 5));
      const w = {};
      (c.pieces || []).forEach(pc => { w[pc.piece] = String(pc.weight_kg); });
      setCalibTypeWeights(w);
    } else {
      setCalibTypeTotal(''); setCalibTypeTolerance('5'); setCalibTypeWeights({});
    }
    setShowCalibType(true);
  };

  const saveCalibType = async () => {    setError('');
    const total = parseFloat(calibTypeTotal) || 0;
    if (total <= 0) { setError('Informe o peso total da carcaça de referência'); return; }
    const pieces = currentPieces
      .map(p => ({ piece: p, weight_kg: parseFloat(calibTypeWeights[p]) || 0 }))
      .filter(x => x.weight_kg > 0);
    if (pieces.length === 0) { setError('Informe o peso de ao menos uma peça'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/beef/type-calibration`, {
        carcass_type: carcassType,
        total_weight: total,
        tolerance_percent: parseFloat(calibTypeTolerance) || 0,
        pieces,
      }, { withCredentials: true });
      setShowCalibType(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar calibração');
    } finally { setLoading(false); }
  };

  const deleteCalibType = async () => {
    if (!window.confirm(`Excluir a calibração do tipo "${(CARCASS_TYPES.find(t => t.key === carcassType) || {}).label}"?`)) return;
    setError('');
    setLoading(true);
    try {
      await axios.delete(`${API}/beef/type-calibration/${carcassType}`, { withCredentials: true });
      setShowCalibType(false);
      setCalibTypeTotal(''); setCalibTypeTolerance('5'); setCalibTypeWeights({});
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao excluir calibração');
    } finally { setLoading(false); }
  };

  const handleCutSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        name: cutForm.name,
        yield_percent: parseFloat(cutForm.yield_percent),
        sale_price_kg: parseFloat(cutForm.sale_price_kg),
        product_code: cutForm.product_code.trim() || null,
        notes: cutForm.notes
      };
      if (editingCut) {
        await axios.put(`${API}/beef/cuts/${editingCut}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/beef/cuts`, payload, { withCredentials: true });
      }
      load();
      setCutForm({ name: '', yield_percent: '', sale_price_kg: '', product_code: '', notes: '' });
      setEditingCut(null);
      setShowCutForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar peça');
    } finally { setLoading(false); }
  };

  const handleArrivalSubmit = async (e) => {
    e.preventDefault();
    const arrivalCost = (parseFloat(arrivalForm.total_weight) || 0) * (parseFloat(arrivalForm.cost_per_kg) || 0);
    if (arrivalCost > 0 && !arrivalPay.payment_method) { setError('Selecione a forma de pagamento da nota'); return; }
    if (arrivalPay.payment_method === 'boleto' && !arrivalPay.due_date) { setError('Informe a data de vencimento do boleto'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/beef/arrivals`, {
        arrival_date: new Date(arrivalForm.arrival_date).toISOString(),
        total_weight: parseFloat(arrivalForm.total_weight),
        supplier_name: arrivalForm.supplier_name || null,
        cost_per_kg: parseFloat(arrivalForm.cost_per_kg) || 0,
        note_number: arrivalPay.note_number || null,
        payment_method: arrivalPay.payment_method || null,
        due_date: arrivalPay.due_date ? new Date(arrivalPay.due_date).toISOString() : null,
        notes: arrivalForm.notes,
        carcass_type: entryType,
      }, { withCredentials: true });
      load();
      setArrivalResult(data);
      setArrivalPay({ note_number: '', payment_method: '', due_date: '' });
      setArrivalForm({ arrival_date: new Date().toISOString().substring(0, 10), total_weight: '', supplier_name: '', cost_per_kg: '', notes: '' });
      setShowArrivalForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao lançar entrada');
    } finally { setLoading(false); }
  };

  const handleEditCut = (c) => {
    setCutForm({
      name: c.name,
      yield_percent: c.yield_percent.toString(),
      sale_price_kg: c.sale_price_kg.toString(),
      product_code: c.product_code || '',
      notes: c.notes || ''
    });
    setEditingCut(c.id);
    setShowCutForm(true);
  };

  const handleDeleteCut = async (id) => {
    if (!window.confirm('Excluir esta peça?')) return;
    try {
      await axios.delete(`${API}/beef/cuts/${id}`, { withCredentials: true });
      load();
    } catch (err) { setError('Erro ao excluir'); }
  };

  const totalYield = cuts.reduce((s, c) => s + c.yield_percent, 0);

  const updateCalibCut = (idx, field, value) => {
    setCalibCuts(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };
  const addCalibCut = () => setCalibCuts([...calibCuts, { name: '', weight_kg: '', sale_price_kg: '' }]);
  const removeCalibCut = (idx) => setCalibCuts(calibCuts.filter((_, i) => i !== idx));

  const calibTotalCuts = calibCuts.reduce((s, c) => s + (parseFloat(c.weight_kg) || 0), 0);
  const calibPerda = (parseFloat(calibTotal) || 0) - calibTotalCuts;

  const handleCalibrate = async (e) => {
    e.preventDefault();
    setError('');
    if (!calibTotal || parseFloat(calibTotal) <= 0) { setError('Informe o peso total do bovino'); return; }
    const validCuts = calibCuts.filter(c => c.name && c.weight_kg && c.sale_price_kg);
    if (validCuts.length === 0) { setError('Adicione ao menos uma peça com nome, peso e preço'); return; }
    if (calibTotalCuts > parseFloat(calibTotal)) { setError('Soma dos pesos é maior que o peso total'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/beef/calibrate`, {
        total_weight: parseFloat(calibTotal),
        cuts: validCuts.map(c => ({
          name: c.name,
          weight_kg: parseFloat(c.weight_kg),
          sale_price_kg: parseFloat(c.sale_price_kg)
        })),
        replace_existing: true
      }, { withCredentials: true });
      setCalibResult(data);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao calibrar');
    } finally {
      setLoading(false);
    }
  };

  const resetCalibration = () => {
    setCalibTotal('');
    setCalibCuts([{ name: '', weight_kg: '', sale_price_kg: '' }]);
    setCalibResult(null);
    setError('');
  };

  return (
    <div className="p-8" data-testid="fracionamento-section">
      <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Fracionamento Bovino</h2>
      <p className="text-slate-600 mb-6">Cadastre as peças (% rendimento) e lance entradas de carne. O sistema distribui pelo peso.</p>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Entradas</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{summary.total_arrivals}</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Peso Total</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{summary.total_weight_kg.toFixed(2)} kg</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Custo Total</p>
            <p className="text-2xl font-bold text-yellow-700 mt-1">R$ {summary.total_cost.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Receita Estimada</p>
            <p className="text-2xl font-bold text-green-600 mt-1">R$ {summary.estimated_revenue.toFixed(2)}</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="grid w-full md:w-[800px] grid-cols-4">
          <TabsTrigger value="arrivals" data-testid="tab-arrivals">Entradas</TabsTrigger>
          <TabsTrigger value="calibrate" data-testid="tab-calibrate">Calibração</TabsTrigger>
          <TabsTrigger value="cuts" data-testid="tab-cuts">Peças</TabsTrigger>
        </TabsList>

        <TabsContent value="arrivals" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowArrivalForm(!showArrivalForm)} className="bg-red-600 hover:bg-red-700" data-testid="add-arrival-btn">
              <Scale size={20} className="mr-2" /> Nova Entrada de Carne
            </Button>
          </div>

          {showArrivalForm && (
            <div className="bg-white rounded-md border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Lançar Entrada de Carne</h2>
              {/* Tipo de carcaça: Peça Avulsa (rendimento das peças) ou tipo calibrado (Dianteiro/Traseiro/Vaca Casada/Gorda) */}
              <div className="mb-4" data-testid="entry-type-selector">
                <Label className="mb-2 block">Tipo de Carcaça *</Label>
                <div className="flex flex-wrap gap-2">
                  {[{ key: 'peca', label: 'Peça Avulsa' }, ...CARCASS_TYPES.map(t => ({ key: t.key, label: t.label }))].map(t => {
                    const active = entryType === t.key;
                    const calibrated = t.key === 'peca' ? cuts.length > 0 : !!typeCalibs[t.key];
                    return (
                      <button key={t.key} type="button" onClick={() => { setEntryType(t.key); if (t.key !== 'peca') setCarcassType(t.key); setError(''); }}
                        className={`px-4 py-2 rounded-md border-2 text-sm font-bold transition-colors ${active ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-red-400'}`}
                        data-testid={`entry-type-${t.key}`}>
                        {t.label}{active ? ' ✓' : ''}{!calibrated && t.key !== 'peca' ? ' ·  não calibrado' : ''}
                      </button>
                    );
                  })}
                </div>
                {entryType !== 'peca' && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs" data-testid="entry-type-calib-status">
                    {typeCalibs[entryType]
                      ? <span className="text-emerald-700 font-medium">Calibrado (ref. {typeCalibs[entryType].reference_total_weight} kg) — o peso total será distribuído pelo rendimento calibrado. Cada peça entra no MESMO produto/código de balança, em qualquer tipo.</span>
                      : <span className="text-amber-700 font-medium">Este tipo ainda não foi calibrado — calibre pesando uma carcaça de referência.</span>}
                    <Button type="button" variant="outline" size="sm" onClick={openCalibType} data-testid="entry-calib-type-btn">
                      <Scale size={14} className="mr-1" /> {typeCalibs[entryType] ? 'Recalibrar tipo' : 'Calibrar tipo'}
                    </Button>
                  </div>
                )}
              </div>
              {entryType === 'peca' && cuts.length === 0 ? (
                <Alert variant="destructive"><AlertDescription>Cadastre ao menos uma peça com % de rendimento antes de lançar entrada (ou selecione um tipo de carcaça calibrado).</AlertDescription></Alert>
              ) : (
                <form onSubmit={handleArrivalSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Input type="date" value={arrivalForm.arrival_date} onChange={(e) => setArrivalForm({ ...arrivalForm, arrival_date: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Peso Total (kg) *</Label>
                    <Input type="number" step="0.01" value={arrivalForm.total_weight} onChange={(e) => setArrivalForm({ ...arrivalForm, total_weight: e.target.value })} placeholder="Ex: 170" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Fornecedor</Label>
                    <Input value={arrivalForm.supplier_name} onChange={(e) => setArrivalForm({ ...arrivalForm, supplier_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Custo por Kg (R$)</Label>
                    <Input type="number" step="0.01" value={arrivalForm.cost_per_kg} onChange={(e) => setArrivalForm({ ...arrivalForm, cost_per_kg: e.target.value })} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Observações</Label>
                    <Input value={arrivalForm.notes} onChange={(e) => setArrivalForm({ ...arrivalForm, notes: e.target.value })} />
                  </div>
                  <PaymentFields value={arrivalPay} onChange={setArrivalPay} totalCost={(parseFloat(arrivalForm.total_weight) || 0) * (parseFloat(arrivalForm.cost_per_kg) || 0)} costPerKg={parseFloat(arrivalForm.cost_per_kg) || 0} testPrefix="arrival" />
                  {error && <div className="md:col-span-2"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
                  {entryType === 'peca' ? (
                    <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                      <p className="font-bold text-blue-900 mb-1">⚡ Sincronização automática com Produtos:</p>
                      <p className="text-blue-700">• O peso será distribuído entre as peças cadastradas conforme o % de rendimento</p>
                      <p className="text-blue-700">• Cada peça gerará automaticamente um produto na categoria <strong>Açougue</strong> (ou atualiza o estoque se já existir)</p>
                      <p className="text-blue-700">• As peças ficam prontas para venda no PDV imediatamente</p>
                      <p className="text-blue-700 mt-1">Total cadastrado: <strong>{totalYield.toFixed(2)}%</strong></p>
                    </div>
                  ) : (
                    <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded p-3 text-sm" data-testid="entry-type-preview">
                      <p className="font-bold text-blue-900 mb-1">⚡ Distribuição pela calibragem — {(CARCASS_TYPES.find(t => t.key === entryType) || {}).label}</p>
                      {typeCalibs[entryType] ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 text-blue-800">
                          {(typeCalibs[entryType].pieces || []).filter(pc => pc.yield_percent > 0).map(pc => (
                            <span key={pc.piece} className="capitalize">{pc.piece}: <strong>{((parseFloat(arrivalForm.total_weight) || 0) * pc.yield_percent / 100).toFixed(3)} kg</strong> <span className="text-blue-500">({pc.yield_percent.toFixed(1)}%)</span></span>
                          ))}
                        </div>
                      ) : <p className="text-amber-700">Calibre o tipo para ver a distribuição.</p>}
                      <p className="text-blue-700 mt-1">• Soma ao estoque do produto já cadastrado de cada peça (mesmo código de balança em todos os tipos) — não cria produtos novos.</p>
                    </div>
                  )}
                  <div className="md:col-span-2 flex gap-2">
                    <Button type="submit" disabled={loading || (entryType !== 'peca' && !typeCalibs[entryType])} className="bg-red-600 hover:bg-red-700" data-testid="arrival-submit">{loading ? 'Salvando...' : 'Lançar Entrada'}</Button>
                    <Button type="button" variant="outline" onClick={() => setShowArrivalForm(false)}>Cancelar</Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {arrivalResult && (
            <Alert className="mb-4 bg-emerald-50 border-emerald-300" data-testid="arrival-result">
              <AlertDescription className="text-emerald-900">
                Entrada lançada{arrivalResult.carcass_type && arrivalResult.carcass_type !== 'peca' ? ` (${(CARCASS_TYPES.find(t => t.key === arrivalResult.carcass_type) || {}).label})` : ''}: <strong>{arrivalResult.products_created || 0}</strong> produto(s) criado(s) · <strong>{arrivalResult.products_updated || 0}</strong> com estoque atualizado · Custo R$ {Number(arrivalResult.total_cost || 0).toFixed(2)}
                {arrivalResult.not_found?.length > 0 && (
                  <p className="text-amber-800 mt-1" data-testid="arrival-not-found">Sem produto correspondente (não lançadas): {arrivalResult.not_found.map(n => n.piece).join(', ')} — vincule o código de balança na aba Peças.</p>
                )}
                <PayableResult payable={arrivalResult.payable} />
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-4">
            {arrivals.length === 0 && (
              <div className="bg-white rounded-md border border-slate-200 p-8 text-center text-slate-400">
                Nenhuma entrada lançada
              </div>
            )}
            {arrivals.map((a) => (
              <div key={a.id} className="bg-white rounded-md border border-slate-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Beef size={20} className="text-red-600" /> {a.total_weight} kg de carne</h3>
                    <p className="text-sm text-slate-500">{new Date(a.arrival_date).toLocaleDateString('pt-BR')} · {a.supplier_name || 'Sem fornecedor'}</p>
                  </div>
                  <div className="text-right flex items-start gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Receita estimada</p>
                      <p className="text-2xl font-bold text-green-600">R$ {a.estimated_total_revenue.toFixed(2)}</p>
                      <p className="text-xs text-slate-500">Custo: R$ {a.total_cost.toFixed(2)}</p>
                    </div>
                    {(user?.role === 'admin' || user?.role === 'manager') && (
                      <button
                        onClick={() => setDeleteArrival(a)}
                        className="text-red-600 hover:bg-red-50 p-2 rounded transition-colors"
                        title="Excluir entrada de fracionamento"
                        data-testid={`delete-arrival-${a.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-2 py-2 text-left text-xs uppercase tracking-wider text-slate-500">Peça</th>
                      <th className="px-2 py-2 text-right text-xs uppercase tracking-wider text-slate-500">% Rend.</th>
                      <th className="px-2 py-2 text-right text-xs uppercase tracking-wider text-slate-500">Peso (kg)</th>
                      <th className="px-2 py-2 text-right text-xs uppercase tracking-wider text-slate-500">Preço/kg</th>
                      <th className="px-2 py-2 text-right text-xs uppercase tracking-wider text-slate-500">Receita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.cuts.map((c, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-2 py-2 font-medium">{c.cut_name}</td>
                        <td className="px-2 py-2 text-right">{c.yield_percent.toFixed(2)}%</td>
                        <td className="px-2 py-2 text-right font-medium text-red-600">{c.weight_kg.toFixed(3)} kg</td>
                        <td className="px-2 py-2 text-right">R$ {c.sale_price_kg.toFixed(2)}</td>
                        <td className="px-2 py-2 text-right font-bold text-green-600">R$ {c.estimated_revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="calibrate" className="mt-4">          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
            <h3 className="font-bold text-blue-900 mb-1">Pesagem Calibradora</h3>
            <p className="text-sm text-blue-800">
              Pese o bovino inteiro e cada peça (Alcatra, Picanha, Costela, etc) e digite os valores abaixo.
              O sistema calcula o % de rendimento automaticamente e atualiza o catálogo.
            </p>
          </div>

          {calibResult ? (
            <div className="bg-white rounded-md border border-green-200 p-6">
              <h3 className="text-2xl font-bold text-green-700 mb-3">Calibração Concluída</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-xs uppercase font-bold text-blue-700">Peso Total</p>
                  <p className="text-xl font-bold text-blue-900">{calibResult.total_weight} kg</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-xs uppercase font-bold text-green-700">Peso Aproveitado</p>
                  <p className="text-xl font-bold text-green-900">{calibResult.total_cuts_weight} kg</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-xs uppercase font-bold text-red-700">Perda</p>
                  <p className="text-xl font-bold text-red-900">{calibResult.perda_kg} kg ({calibResult.perda_percent}%)</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-xs uppercase font-bold text-yellow-700">Peças Cadastradas</p>
                  <p className="text-xl font-bold text-yellow-900">{calibResult.cuts.length}</p>
                </div>
              </div>
              <table className="w-full text-sm border border-slate-200 rounded mb-4">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold uppercase text-xs">Peça</th>
                    <th className="px-3 py-2 text-right font-bold uppercase text-xs">% Rendimento</th>
                    <th className="px-3 py-2 text-right font-bold uppercase text-xs">Preço/kg</th>
                  </tr>
                </thead>
                <tbody>
                  {calibResult.cuts.map((c, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium">{c.name}</td>
                      <td className="px-3 py-2 text-right font-bold text-red-600">{c.yield_percent.toFixed(2)}%</td>
                      <td className="px-3 py-2 text-right text-blue-600 font-bold">R$ {c.sale_price_kg.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button onClick={resetCalibration} className="bg-blue-600 hover:bg-blue-700">Nova Calibração</Button>
            </div>
          ) : (
            <div className="bg-white rounded-md border border-slate-200 p-6">
              <form onSubmit={handleCalibrate} className="space-y-4">
                <div className="space-y-2 md:w-1/2">
                  <Label>Peso Total do Bovino (kg) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={calibTotal}
                    onChange={(e) => setCalibTotal(e.target.value)}
                    placeholder="Ex: 250"
                    required
                    data-testid="calib-total-weight"
                  />
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-slate-900">Peças do Bovino</h3>
                    <Button type="button" onClick={addCalibCut} className="bg-blue-600 hover:bg-blue-700">
                      <Plus size={16} className="mr-1" /> Adicionar Peça
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {calibCuts.map((c, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid={`calib-row-${idx}`}>
                        <div className="md:col-span-5 space-y-1">
                          <Label className="text-xs">Peça (ex: Alcatra, Picanha)</Label>
                          <Input value={c.name} onChange={(e) => updateCalibCut(idx, 'name', e.target.value)} placeholder="Nome da peça" data-testid={`calib-name-${idx}`} />
                        </div>
                        <div className="md:col-span-3 space-y-1">
                          <Label className="text-xs">Peso (kg)</Label>
                          <Input type="number" step="0.01" value={c.weight_kg} onChange={(e) => updateCalibCut(idx, 'weight_kg', e.target.value)} placeholder="0.00" data-testid={`calib-weight-${idx}`} />
                        </div>
                        <div className="md:col-span-3 space-y-1">
                          <Label className="text-xs">Preço/kg (R$)</Label>
                          <Input type="number" step="0.01" value={c.sale_price_kg} onChange={(e) => updateCalibCut(idx, 'sale_price_kg', e.target.value)} placeholder="0.00" data-testid={`calib-price-${idx}`} />
                        </div>
                        <div className="md:col-span-1">
                          <Button type="button" onClick={() => removeCalibCut(idx)} variant="outline" className="w-full text-red-600 hover:bg-red-50 border-red-200" disabled={calibCuts.length === 1}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {calibTotal && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-xs uppercase font-bold text-yellow-700">Peso Total</span><div className="font-bold text-yellow-900">{parseFloat(calibTotal).toFixed(2)} kg</div></div>
                    <div><span className="text-xs uppercase font-bold text-yellow-700">Soma das Peças</span><div className="font-bold text-yellow-900">{calibTotalCuts.toFixed(2)} kg</div></div>
                    <div><span className="text-xs uppercase font-bold text-yellow-700">Perda Estimada</span><div className={`font-bold ${calibPerda < 0 ? 'text-red-600' : 'text-yellow-900'}`}>{calibPerda.toFixed(2)} kg</div></div>
                  </div>
                )}

                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

                <Alert className="border-orange-200 bg-orange-50">
                  <AlertDescription className="text-orange-800 text-sm">
                    Esta ação substituirá <strong>todas as peças cadastradas</strong> no catálogo.
                  </AlertDescription>
                </Alert>

                <div className="flex gap-2">
                  <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700" data-testid="calib-submit">
                    <Scale size={16} className="mr-2" />
                    {loading ? 'Calibrando...' : 'Calibrar e Salvar'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetCalibration}>Limpar</Button>
                </div>
              </form>
            </div>
          )}
        </TabsContent>

        <TabsContent value="cuts" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowCutForm(!showCutForm)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-cut-btn">
              <Plus size={20} className="mr-2" /> Nova Peça
            </Button>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
            <p className="text-sm text-yellow-900">
              <strong>Total de Rendimento Cadastrado:</strong> {totalYield.toFixed(2)}%
              {totalYield > 100 && <span className="text-red-600 ml-2">(Atenção: superior a 100%)</span>}
              {totalYield < 100 && totalYield > 0 && <span className="text-yellow-700 ml-2">({(100 - totalYield).toFixed(2)}% será considerado perda)</span>}
            </p>
          </div>

          {showCutForm && (
            <div className="bg-white rounded-md border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{editingCut ? 'Editar Peça' : 'Nova Peça'}</h2>
              <form onSubmit={handleCutSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Peça *</Label>
                  <Input value={cutForm.name} onChange={(e) => setCutForm({ ...cutForm, name: e.target.value })} placeholder="Ex: Picanha, Alcatra" required />
                </div>
                <div className="space-y-2">
                  <Label>% Rendimento *</Label>
                  <Input type="number" step="0.01" value={cutForm.yield_percent} onChange={(e) => setCutForm({ ...cutForm, yield_percent: e.target.value })} placeholder="Ex: 12.5" required />
                </div>
                <div className="space-y-2">
                  <Label>Preço de Venda por Kg (R$) *</Label>
                  <Input type="number" step="0.01" value={cutForm.sale_price_kg} onChange={(e) => setCutForm({ ...cutForm, sale_price_kg: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Código de venda (balança/PDV)</Label>
                  <Input value={cutForm.product_code} onChange={(e) => setCutForm({ ...cutForm, product_code: e.target.value })} placeholder="Ex: 101" data-testid="cut-product-code-input" />
                  <p className="text-[11px] text-slate-500">
                    {(() => { const pr = productList.find(x => cutForm.product_code.trim() && (x.code === cutForm.product_code.trim() || x.barcode === cutForm.product_code.trim())); return pr ? <span className="text-emerald-700">✓ {pr.name} · estoque {Number(pr.stock_quantity || 0).toFixed(3)} kg</span> : (cutForm.product_code.trim() ? <span className="text-amber-700">Código não encontrado em Produtos</span> : 'O estoque das entradas desta peça será somado no produto com este código.'); })()}
                  </p>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Observações</Label>
                  <Input value={cutForm.notes} onChange={(e) => setCutForm({ ...cutForm, notes: e.target.value })} />
                </div>
                {error && <div className="md:col-span-2"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
                <div className="md:col-span-2 flex gap-2">
                  <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">{loading ? 'Salvando...' : 'Salvar'}</Button>
                  <Button type="button" variant="outline" onClick={() => { setShowCutForm(false); setEditingCut(null); setCutForm({ name: '', yield_percent: '', sale_price_kg: '', product_code: '', notes: '' }); }}>Cancelar</Button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Peça</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">% Rendimento</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Preço/kg</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Código venda</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {cuts.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">Nenhuma peça cadastrada. Cadastre para distribuir o peso automaticamente.</td></tr>}
                {cuts.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-right font-bold text-red-600">{c.yield_percent.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-right font-bold text-blue-600">R$ {c.sale_price_kg.toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs" data-testid={`cut-code-${c.id}`}>
                      {c.product_code
                        ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">{c.product_code}</span>
                        : <span className="text-amber-700">sem código</span>}
                      {c.product_name && <span className="ml-1 text-slate-500">→ {c.product_name}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEditCut(c)} className="text-blue-600 hover:text-blue-700"><Edit size={16} /></button>
                        <button onClick={() => handleDeleteCut(c.id)} className="text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal: Calibrar rendimento por tipo de carcaça */}
      {showCalibType && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(() => setShowCalibType(false))}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="calib-type-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Scale size={20} className="text-red-600" /> Calibrar rendimento — {(CARCASS_TYPES.find(t => t.key === carcassType) || {}).label}
              </h3>
              <button onClick={() => setShowCalibType(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Pese uma carcaça de referência: informe o peso total e o peso de cada peça. O sistema calcula o % de rendimento esperado e salva para este tipo.</p>

            <TypePiecesManager
              key={carcassType}
              carcassType={carcassType}
              pieces={currentPieceObjs}
              productList={productList}
              onSaved={(pieces) => setTypePieces(prev => ({ ...prev, [carcassType]: pieces }))}
              sources={CARCASS_TYPES.filter(t => t.key !== carcassType).map(t => ({
                key: t.key, label: t.label,
                pieces: typePieces[t.key] ? typePieces[t.key].map(p => ({ name: p.name, code: p.code || '' })) : t.pieces.map(n => ({ name: n, code: '' })),
              }))}
            />

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500">Peso total da carcaça (kg) *</Label>
                <Input type="number" step="0.001" value={calibTypeTotal} onChange={(e) => setCalibTypeTotal(e.target.value)} placeholder="Ex: 120" data-testid="calib-type-total" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Tolerância (%)</Label>
                <Input type="number" step="0.5" value={calibTypeTolerance} onChange={(e) => setCalibTypeTolerance(e.target.value)} placeholder="5" data-testid="calib-type-tolerance" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              {currentPieces.map((p) => {
                const w = parseFloat(calibTypeWeights[p]) || 0;
                const tot = parseFloat(calibTypeTotal) || 0;
                const yp = (w > 0 && tot > 0) ? (w / tot * 100) : null;
                return (
                  <div key={p} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium capitalize block truncate">{p}</span>
                      {yp != null && <span className="text-[11px] text-blue-600 font-bold">{yp.toFixed(2)}%</span>}
                    </div>
                    <Input
                      type="number" step="0.001" placeholder="kg"
                      value={calibTypeWeights[p] || ''}
                      onChange={(e) => setCalibTypeWeights({ ...calibTypeWeights, [p]: e.target.value })}
                      className="w-28 text-right"
                      data-testid={`calib-type-piece-${p.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                    />
                  </div>
                );
              })}
            </div>

            {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCalibType(false)} className="flex-1" data-testid="calib-type-cancel">Cancelar</Button>
              {currentCalib && (
                <Button variant="outline" onClick={deleteCalibType} disabled={loading} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="calib-type-delete">
                  <Trash2 size={16} className="mr-1" /> Excluir
                </Button>
              )}
              <Button onClick={saveCalibType} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="calib-type-save">
                {loading ? 'Salvando...' : 'Salvar Calibração'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Excluir fracionamento com justificativa */}
      {deleteArrival && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(() => { setDeleteArrival(null); setDeleteReason(''); })}>
          <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="delete-arrival-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <AlertOctagon size={20} className="text-red-600" /> Excluir Fracionamento
              </h3>
              <button onClick={() => { setDeleteArrival(null); setDeleteReason(''); }} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="bg-red-50 border border-red-300 rounded p-3 mb-3 text-sm">
              <p><strong>{deleteArrival.total_weight} kg</strong> · {new Date(deleteArrival.arrival_date).toLocaleDateString('pt-BR')}</p>
              <p className="text-xs text-slate-600 mt-1">Receita estimada: R$ {deleteArrival.estimated_total_revenue?.toFixed(2)}</p>
              <p className="text-xs text-amber-700 mt-2">⚠ Esta ação remove a entrada e <strong>também os produtos derivados</strong> (peças de açougue criadas a partir desta entrada).</p>
            </div>
            <Label>Justificativa (mín. 3 caracteres) *</Label>
            <Input
              autoFocus
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && deleteReason.trim().length >= 3) {
                  e.preventDefault();
                  try {
                    await axios.delete(`${API}/beef/arrivals/${deleteArrival.id}?reason=${encodeURIComponent(deleteReason.trim())}&delete_products=true`, { withCredentials: true });
                    setDeleteArrival(null); setDeleteReason('');
                    load();
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Erro ao excluir');
                  }
                }
              }}
              placeholder="Ex: Cadastro errado, peso incorreto..."
              data-testid="delete-arrival-reason"
            />
            <p className="text-[10px] text-slate-500 mt-1">Pressione Enter para confirmar</p>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" onClick={() => { setDeleteArrival(null); setDeleteReason(''); }} className="flex-1" data-testid="delete-arrival-cancel">Cancelar</Button>
              <Button
                disabled={deleteReason.trim().length < 3}
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/beef/arrivals/${deleteArrival.id}?reason=${encodeURIComponent(deleteReason.trim())}&delete_products=true`, { withCredentials: true });
                    setDeleteArrival(null); setDeleteReason('');
                    load();
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Erro ao excluir');
                  }
                }}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40"
                data-testid="delete-arrival-confirm"
              >
                Confirmar Exclusão
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
